import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  getWebUsers,
  deleteWebUser,
} from "../../api/webUserAPI";
import { useTranslation } from "react-i18next";
import InviteUserDialog from "./InviteUserDialog";
import EditUserDialog from "./EditUserDialog";
import PasswordResetDialog from "./PasswordResetDialog";
import CreateClientDialog from "./CreateClientDialog";

const formatDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString("tr") + " " + d.toLocaleTimeString("tr");
};

export default function UsersDataGrid() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Dialog open states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getWebUsers();
        setUsers(response || []);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err.message || "Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    setRows(
      users
        .filter((u) => u.username !== "admin@letta.com.tr")
        .map((u) => ({
          ...u,
          date: formatDate(u.createdTimestamp),
        }))
    );
  }, [users]);

  const refreshUsers = async () => {
    try {
      const response = await getWebUsers();
      setUsers(response || []);
    } catch (err) {
      console.error("Error refreshing users:", err);
    }
  };

  const handleMenuClick = (event, rowId) => {
    console.log("Selected Row ID:", rowId);
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const columns = useMemo(() => [
    {
      field: "username",
      headerName: t("users.dataGrid.username"),
      width: 150,
      flex: 1.2,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "firstName",
      headerName: t("users.dataGrid.firstName"),
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "lastName",
      headerName: t("users.dataGrid.lastName"),
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "email",
      headerName: t("users.dataGrid.email"),
      width: 200,
      flex: 1.2,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group1",
      headerName: t("users.dataGrid.group1"),
      width: 120,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group2",
      headerName: t("users.dataGrid.group2"),
      width: 120,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group3",
      headerName: t("users.dataGrid.group3"),
      width: 120,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "date",
      headerName: t("users.dataGrid.date"),
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "operations",
      headerName: t("users.dataGrid.operations"),
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={(event) => handleMenuClick(event, params.row.id)}
            size="small"
          >
            <MoreHorizIcon />
          </IconButton>
        );
      },
    },
  ], [t]);

  const handleEdit = () => {
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (window.confirm(t("users.messages.deleteConfirm"))) {
      try {
        setLoading(true);
        await deleteWebUser(selectedRowId);
        console.log("User deleted successfully");
        await refreshUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        alert(
          t("users.messages.deleteFailed") +
            ": " +
            (error.message || "Unknown error")
        );
      } finally {
        setLoading(false);
      }
    }
    handleMenuClose();
  };

  const handleResetPassword = () => {
    console.log("Selected Row ID (User ID):", selectedRowId);
    setPasswordResetDialogOpen(true);
    handleMenuClose();
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const selectedUser = users.find((u) => u.id === selectedRowId) || null;

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Box sx={{ maxWidth: { sm: "100%", md: "1700px" } }}>
        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6" fontWeight="bold" sx={{ opacity: 0 }}>
                Placeholder
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ opacity: 0 }}>
                Placeholder
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {t("users.header")}
              </Typography>
              <Box flex={1} />
              <Button
                variant="contained"
                color="primary"
                onClick={() => setAddDialogOpen(true)}
                sx={{ height: 45, width: 200 }}
              >
                {t("users.controls.addUser")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ height: "75%", width: "100%", position: "relative" }}>
        {loading && rows.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" align="center" sx={{ my: 2 }}>
            {typeof error === "string" ? error : t("users.messages.fetchError")}
          </Typography>
        )}

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          slots={{ toolbar: GridToolbar }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100 },
            },
            filter: {
              filterModel: {
                items: [{ field: "code", operator: "contains", value: "" }],
              },
            },
          }}
          loading={loading && rows.length === 0}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("common.edit")} />
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("common.delete")} />
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          <ListItemIcon>
            <RestartAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("users.menu.resetPassword")} />
        </MenuItem>
      </Menu>

      <InviteUserDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={refreshUsers}
      />

      <EditUserDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={refreshUsers}
        user={selectedUser}
      />

      <PasswordResetDialog
        open={passwordResetDialogOpen}
        onClose={() => setPasswordResetDialogOpen(false)}
        userId={selectedRowId}
        onSnackbar={setSnackbar}
      />

      <CreateClientDialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        onSnackbar={setSnackbar}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
